import React from "react";
import { useTranslation } from "react-i18next";
import { Link2, Sparkles, Wand2 } from "lucide-react";
import { DirectorySelect } from "../directory/DirectorySelect";
import type { EntitySnapshot, Partner } from "../../types";
import type { IntakeLink } from "../../types/containerOrderIntake";

export type IntakeLinkAction =
  | { type: "candidate"; id: string }
  | { type: "pick"; snapshot: EntitySnapshot }
  | { type: "clear" };

interface IntakeLinkPanelProps {
  link: IntakeLink;
  /** The id currently on the draft, and the name to show in the picker. */
  selectedId: string | null;
  name: string;
  /** What the Add form starts from — the company as the document gives it. */
  createDefaults: Partial<Partner>;
  readOnly?: boolean;
  onAction: (action: IntakeLinkAction) => void;
}

const STATUS_TONE: Record<IntakeLink["status"], string> = {
  linked: "bg-green-100 text-green-800",
  suggested: "bg-amber-100 text-amber-800",
  missing: "bg-red-100 text-red-700",
};

/**
 * How one company on the order ties to the directory.
 *
 * Most arrive settled: matched, or — when the order described a company the
 * directory did not have — added to it on arrival, which the panel says. The
 * rest are the reviewer's: confirm one of the matcher's candidates, pick any
 * record, or Add it — the directory's own form opens prefilled from the
 * document, the record is saved straight away, and it is selected. Why it was
 * not added on arrival is shown, so the reviewer knows what the form still
 * needs. Accept itself never writes to the directories.
 */
const IntakeLinkPanel: React.FC<IntakeLinkPanelProps> = ({
  link,
  selectedId,
  name,
  createDefaults,
  readOnly,
  onAction,
}) => {
  const { t } = useTranslation("containerOrderIntake");
  const directory = t(`link.directory.${link.directory}`);
  const auto = link.autoCreate;

  // Only while it is still the record on the order — a reviewer may have
  // picked another since.
  const added = auto?.created && link.status === "linked" && auto.recordId === selectedId;
  const notAdded = auto && !auto.created && link.status !== "linked" ? auto : null;

  return (
    <div className="rounded-md border border-[#EFEDE7] bg-[#FBFAF7] p-2.5 space-y-2 text-[12px]">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${STATUS_TONE[link.status]}`}
        >
          <Link2 className="h-3 w-3" />
          {t(`link.status.${link.status}`, { directory })}
        </span>
        {added && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-800">
            <Wand2 className="h-3 w-3" />
            {t("link.autoCreated", { directory })}
          </span>
        )}
      </div>

      {notAdded && (
        <div className="text-[#4A5550]">
          {t("link.notAutoCreated", {
            reason: t(`link.autoCreateReason.${notAdded.code}`, {
              directory,
              field: notAdded.field ? t(`link.autoCreateField.${notAdded.field}`, notAdded.field) : "",
              defaultValue: notAdded.message,
            }),
          })}
        </div>
      )}

      {!readOnly && link.status === "suggested" && link.candidates.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-[#4A5550]">
            <Sparkles className="h-3.5 w-3.5 text-amber-600" />
            {t("link.candidatesTitle")}
          </div>
          {link.candidates.map((candidate) => (
            <button
              key={candidate._id}
              type="button"
              onClick={() => onAction({ type: "candidate", id: candidate._id })}
              className="w-full text-left rounded border border-[#E5E3DC] bg-white px-2 py-1.5 hover:border-[#C97A3C]"
            >
              <span className="font-medium text-[#0B0F0E]">{candidate.companyName}</span>
              <span className="text-[#8A9590]">
                {[candidate.registerationCode, candidate.street, candidate.city]
                  .filter(Boolean)
                  .map((part) => ` · ${part}`)}
              </span>
            </button>
          ))}
        </div>
      )}

      {!readOnly && (
        <>
          <DirectorySelect
            source={link.directory}
            selectedId={link.status === "linked" ? selectedId : null}
            value={link.status === "linked" ? name : ""}
            onSelect={(snapshot) => onAction({ type: "pick", snapshot })}
            onClear={() => onAction({ type: "clear" })}
            placeholder={t("link.pickPlaceholder")}
            createDefaults={createDefaults}
          />
          {link.status !== "linked" && (
            <div className="text-[#8A9590]">
              {t("link.addHint", { directory: t(`link.directory.${link.directory}`) })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IntakeLinkPanel;
